/**
 * Fond de marque : dégradé, halos et anneaux tirés de la primaire et de
 * l'accent de l'organisation (#79). Partagé par la page de connexion et le
 * bandeau du tableau de bord, pour que ce soit le même motif d'un écran à
 * l'autre.
 *
 * C'est une valeur CSS `background`, et non des éléments positionnés : le
 * bandeau du tableau de bord ne peut pas devenir référent de position sans
 * décrocher son illustration.
 *
 * Chaque teinte est mêlée au FOND du thème, jamais au transparent : l'accent
 * reste clair en thème sombre, et posé tel quel il y faisait des taches.
 */

/**
 * Part maximale de primaire et d'accent dans les aplats du fond (0..1), et
 * présence de l'anneau calé dans l'angle bas-gauche.
 */
export type BackdropTint = { primary: number, accent: number, cornerRing: boolean }

/** Page de connexion : aucun texte n'est posé sur le fond, la marque peut s'affirmer. */
export const LOGIN_TINT: BackdropTint = { primary: 0.4, accent: 0.35, cornerRing: true }

/**
 * Fond sous du texte, y compris le gris secondaire. Plafonds épinglés dans
 * `theme-contrast.spec.ts` : au-delà de 8 % de primaire, une marque sombre fait
 * passer le texte secondaire sous AA en thème clair.
 */
export const TEXT_SAFE_TINT: BackdropTint = { primary: 0.08, accent: 0.2, cornerRing: false }

/**
 * Aplat de primaire mêlée au fond, sans motif : pour un bandeau dont le texte
 * peut passer n'importe où, y compris là où le motif poserait ses anneaux (#307).
 */
export function brandTint(share: number): string {
  return tinted('--color-primary', share)
}

function tinted(token: '--color-primary' | '--color-accent', share: number): string {
  return `color-mix(in oklab, var(${token}) ${Math.round(share * 100)}%, var(--color-background))`
}

/** Traits des anneaux : ils ne portent aucun texte, la marque y est franche. */
const RING = 'color-mix(in oklab, var(--color-primary) 45%, transparent)'

export function brandBackdrop({ primary, accent, cornerRing }: BackdropTint): string {
  return [
    `radial-gradient(circle at 84% 22%, ${tinted('--color-accent', accent)} 0 18px, transparent 19px)`,
    `radial-gradient(circle at 90% 14%, transparent 0 62px, ${RING} 62px 64px, transparent 65px)`,
    // Anneau calé dans l'angle. Sous du texte (bandeau du tableau de bord), il
    // dépassait en arc entre la carte et ses tuiles : on l'y retire (#106).
    ...(cornerRing ? [`radial-gradient(circle at 0% 100%, transparent 0 88px, ${RING} 88px 90px, transparent 91px)`] : []),
    `radial-gradient(36rem circle at 0% 0%, ${tinted('--color-primary', primary)}, transparent 70%)`,
    `radial-gradient(40rem circle at 100% 100%, ${tinted('--color-accent', accent)}, transparent 70%)`,
    `linear-gradient(135deg, ${tinted('--color-primary', primary / 2)}, ${tinted('--color-accent', accent * 0.7)})`,
  ].join(', ')
}
